import fileSize from 'filesize';
import throttle from 'lodash/throttle';

function url(pathname: string, query?: Record<string, any>) {
  const _url = new URL(pathname, window.location.origin);
  for (const key in query) {
    const value = query[key];
    if (value !== undefined)
      _url.searchParams.append(key, typeof value === 'string' ? value : value.toString());
  }
  return _url.href;
}

function getCookie(name: string) {
  const cookieArr = document.cookie.split(';');

  for (let i = 0; i < cookieArr.length; i++) {
    const cookiePair = cookieArr[i].split('=');

    if (name === cookiePair[0].trim()) {
      return decodeURIComponent(cookiePair[1]);
    }
  }

  return null;
}

function createWatchdog(ms: number, trigger: () => void) {
  let timeout = setTimeout(trigger, ms);

  function reset() {
    dispose();
    timeout = setTimeout(trigger, ms);
  }

  function dispose() {
    clearTimeout(timeout);
  }

  return { reset, dispose };
}

let id = 0;

export function sendfile(
  args: {
    headers?: Record<string, string>;
    url: string;
    method: string;
    payload: ArrayBuffer | Blob;
    signal?: AbortSignal;
    query?: Record<string, string | number | undefined>;
  },
  watchdogMs: number | undefined,
  progressCallback?: (loaded: number, total: number) => void,
): Promise<{ response: any; status: number; success: boolean }> {
  return new Promise((ok, err) => {
    const prefix = `[sendfile ${(id++).toString().padStart(3)}]`;
    console.debug(prefix, 'start');
    const xhr = new XMLHttpRequest();
    args.signal?.addEventListener('abort', onSignalAbrt);

    // Throttle progress callback to 100ms
    const throttledProgress = progressCallback
      ? throttle(progressCallback, 300, { leading: true, trailing: true })
      : undefined;

    function onAbort() {
      console.debug(prefix, 'abort');
      xhr.abort();
      dispose();
      err({
        response: xhr.response,
        status: xhr.status,
        success: false,
      });
    }

    function dispose() {
      args.signal?.removeEventListener('abort', onSignalAbrt);
      watchdog?.dispose();
    }

    function onSignalAbrt() {
      console.debug(prefix, 'abort due to signal');
      onAbort();
    }

    const watchdog = watchdogMs
      ? createWatchdog(watchdogMs, () => {
          console.debug(prefix, `abort due to watchdog (${watchdogMs}ms)`);
          onAbort();
        })
      : undefined;

    xhr.upload.addEventListener('progress', (event) => {
      watchdog?.reset();
      console.debug(prefix, 'progress', fileSize(event.loaded), '/', fileSize(event.total));
      if (event.lengthComputable) {
        throttledProgress?.(event.loaded, event.total);
      }
    });
    xhr.addEventListener('load', () => {
      console.debug(prefix, 'load');
      const success = xhr.status >= 200 && xhr.status < 300;
      dispose();
      // Flush any trailing throttled progress event
      throttledProgress?.flush?.();
      ok({
        response: xhr.response,
        status: xhr.status,
        success,
      });
    });
    xhr.addEventListener('error', () => {
      console.debug(prefix, 'error');
      dispose();
      throttledProgress?.cancel?.();
      err({
        response: xhr.response,
        status: xhr.status,
        success: false,
      });
    });

    xhr.open(args.method, url(args.url, args.query), true);

    xhr.responseType = 'json';
    for (const header in args.headers) {
      xhr.setRequestHeader(header, args.headers[header]);
    }
    const csrf = getCookie('csrftoken');
    if (csrf) {
      xhr.setRequestHeader('X-CSRFToken', csrf);
    }

    xhr.send(args.payload);
  });
}
